// import styleObjToString from '../helpers/styleObjToString'
import Attrs from '../models/Attrs'
import { generateElementSelectors } from '../styles/selectors'
import renderTemplate from '../helpers/templating'
import {
  addLanguageController,
  computeAllTemplateValues,
  getBaseBuilderVersion,
  getCurrencySymbol,
} from '../helpers/utils'

import type { BaseBuilderVersion, Flow, FlowPage, StripeCheckout2, UserData } from '../models/types'

const CONTROLLER = 'components--stripe-checkout-2'

export default function renderStripeCheckout2(
  component: StripeCheckout2,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow,
  userData?: { [key: string]: unknown }
): string {
  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  attrs
    .addController(CONTROLLER)
    .addDOMClass('StripeCheckout2')
    .addAction(
      `${CONTROLLER}:next->flow#next`,
      `${CONTROLLER}:update->flow#update`,
      `${CONTROLLER}:validate->flow#triggerFormValidation`,
      `${CONTROLLER}:actions->flow#dispatchAction`
    )

  if (!component.elements_to_display || !component.checkout_session)
    return `<div class="StripeCheckout2">Incorrect schema for the Stripe Checkout 2 component</div>`

  const formAttrs = new Attrs(attrs.static_render)
    .addTarget(CONTROLLER, 'paymentForm')
    .add('id', 'payment-form')

  const expressCheckoutContainer = component.elements_to_display.includes('express_checkout')
    ? renderExpressCheckoutContainer(component, attrs.static_render)
    : ``

  const paymentContainer = component.elements_to_display.includes('payment')
    ? renderPaymentContainer(component, attrs.static_render)
    : ``

  const payButton = component.elements_to_display.includes('payment')
    ? renderPayButton(component, page, attrs.static_render, builderVersion, userData)
    : ``

  const messages = renderMessagesContainer(attrs, component)
  const form = `<div ${formAttrs}>${expressCheckoutContainer}${paymentContainer}${payButton}${messages}</div>`

  return `<div ${attrs.toString()}>${form}</div>`
}

function renderPaymentContainer(component: StripeCheckout2, attrsStaticRender: boolean): string {
  const baseId = `stripe-container-${component.id}`
  const attrs = new Attrs(attrsStaticRender)
    .addTarget(CONTROLLER, 'card')
    .add('id', baseId)
    .addDOMClass('payment-box')

  return `<div ${attrs}></div>`
}

function renderExpressCheckoutContainer(
  component: StripeCheckout2,
  attrsStaticRender: boolean
): string {
  const attrs = new Attrs(attrsStaticRender)
    .addTarget(CONTROLLER, 'expressCheckout')
    .addDOMClass('express-checkout')
  return `<div ${attrs}></div>`
}

function renderPayButton(
  component: StripeCheckout2,
  page: FlowPage,
  attrsStaticRender: boolean,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
): string {
  const attrs = new Attrs(attrsStaticRender)
    .addTarget(CONTROLLER, 'button')
    .add('type', 'button')
    .addDOMClass(
      generateElementSelectors({
        elementType: 'StripeCheckout2Button',
        component,
        page,
        elementTags: ['Main'],
        builderVersion,
      })
    )
    .addAction(`click->${CONTROLLER}#attemptPayment`)
  if (component.page_reactive_classes) attrs.addReactiveElement()

  const iconPendingSvg = `<svg style="width: 20px; margin: 0 5px -4px 0; animation: spin 1s linear infinite;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle style="opacity: .25;" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path style="opacity: .75;" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`
  const contentPendingAttrs = new Attrs(attrs.static_render)
    .addTarget(CONTROLLER, 'buttonContentPending')
    .add('style', 'display: none;')
  const contentPayAttrs = new Attrs(attrs.static_render).addTarget(CONTROLLER, 'buttonContentPay')
  const pendingText = `<span>Processing...</span>`
  const iconRightAttrs = new Attrs(attrs.static_render)
    .addIcon('fa', 'chevron-right')
    .add('height', '1em')
    .add('width', '0.66em')
  const payTextAttrs = new Attrs(attrs.static_render).addTarget(CONTROLLER, 'buttonContentPayText')

  //* Add language controller for the pay button text so it updates on language change
  addLanguageController({
    attrs: payTextAttrs,
    component,
    keys: ['pay_button_text'],
    defaultValue: getPayButtonText(component, userData),
    mapKeys: { pay_button_text: 'text' },
  })

  //* Add template controller if the text has {{
  if (component.display?.pay_button_text && component.display?.pay_button_text.includes('{{'))
    payTextAttrs
      .addController('template')
      .addValueIfExists(
        'template',
        'text',
        encodeURIComponent(component.display?.pay_button_text || '')
      )
      .addValueIfExists('template', 'always-rerender', component.always_rerender)

  const payText = `<span ${payTextAttrs}>${getPayButtonText(component, userData)}</span>`

  return `
  <button ${attrs}>
    <span ${contentPendingAttrs}>
      ${iconPendingSvg}
      ${pendingText}
    </span>
    <span ${contentPayAttrs}>
      ${payText}
      <svg ${iconRightAttrs}></svg>
    </span>
  </button>`
}

function renderMessagesContainer(attrs: Attrs, component: StripeCheckout2): string {
  const errAttrs = new Attrs(attrs.static_render)
    .addTarget(CONTROLLER, 'errorContainer')
    .addDOMClass('error-message')
  const errIcon = new Attrs(attrs.static_render).addIcon('fa', 'exclamation-triangle')
  const defaultSuccessMessage = component.display?.success_message || getSuccessMessage(component)
  const successAttrs = new Attrs(attrs.static_render)
    .addTarget(CONTROLLER, 'successContainer')
    .addDOMClass('success-message')
    .addController('template')
    .addValueIfExists('template', 'text', encodeURIComponent(defaultSuccessMessage))
    .addValueIfExists('template', 'always-rerender', component.always_rerender)

  //* Add language controller for the success message so it updates on language change
  addLanguageController({
    attrs: successAttrs,
    component,
    keys: ['success_message'],
    defaultValue: defaultSuccessMessage,
    mapKeys: { success_message: 'text' },
  })

  const errors = `<div ${errAttrs} style="display:none;"><svg ${errIcon}></svg></div>`
  const successes = `<div ${successAttrs} style="display:none;">${getSuccessMessage(component)}</div>`
  return `<div class="payment-messages">${errors}${successes}</div>`
}

// todo - get actual total from the checkout session
function getPayButtonText(component: StripeCheckout2, userData?: { [key: string]: unknown }) {
  if (component.display?.pay_button_text) {
    const payButtonAfterTemplate =
      renderTemplate(component.display?.pay_button_text, userData) || ''
    return payButtonAfterTemplate
  } else if (component.checkout_session.mode === 'setup') return `Save Card Details`

  return `Pay`
}

function getSuccessMessage(component: StripeCheckout2) {
  return component.checkout_session.mode === 'setup'
    ? `Your information has been saved!`
    : component.checkout_session.payment_intent_data?.capture_method === 'manual'
    ? `Your funds have been reserved!`
    : `Your payment has been received!`
}
